import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { QueryResult } from 'utils/api'

import { useFetchApi } from './useFetchApi'

export interface ApiQueryOptions<TResponse, TData> {
  /**
   * Envelope unwrapping (e.g. `{ data }` → `data`), applied AFTER the cache.
   * The cache always stores the raw server response keyed by URL, so two
   * hooks hitting the same URL share one request and one cache entry while
   * each applying their own unwrap. Pass a module-level const so its
   * identity is stable across renders.
   */
  select?: (response: TResponse) => TData
  /**
   * Poll cadence; a predicate over the latest data can stop the poll.
   * NOTE: the predicate receives the RAW cached response (pre-`select`) —
   * TanStack applies `select` at the observer level, after the cache.
   */
  refetchInterval?: number | false | ((data: TResponse | undefined) => number | false)
  /** Override the app-wide freshness defaults (see lib/query-client.ts). */
  staleTime?: number
  gcTime?: number
  refetchOnWindowFocus?: boolean
}

/**
 * TanStack Query shim preserving the hand-rolled `QueryResult<T>` contract
 * every hook in `hooks/api/` exposed before the migration:
 *
 * - `url === null` means "disabled" (the old `if (!id) return` guards):
 *   no fetch, `data` null, `loading` false, `refetch` a no-op.
 * - `loading` is true on first-ever load AND while a key change is showing
 *   the previous payload as a placeholder — exactly the old behavior where
 *   a param change flipped `loading` while stale data stayed rendered.
 * - Remounts paint the last cached payload instantly while
 *   `refetchOnMount: 'always'` + `staleTime: 0` guarantee a revalidation is
 *   already in flight — data is never rendered without a live refetch.
 * - No `error` field, matching the old hooks (which had no `.catch()`):
 *   a failed refetch keeps the previous data and clears `loading`. The
 *   error is available on the underlying query if a consumer ever needs it.
 *
 * Mirrors `useCustomersApi` in apps/customers (`customers-api.ts`).
 */
export function useApiQuery<TResponse, TData = TResponse>(
  url: string | null,
  opts?: ApiQueryOptions<TResponse, TData>,
): QueryResult<TData> {
  const { fetchApi } = useFetchApi()

  const refetchInterval = opts?.refetchInterval
  const query = useQuery<TResponse, Error, TData>({
    queryKey: ['boombox-api', url],
    queryFn: ({ signal }) => fetchApi<TResponse>(url as string, { signal }),
    enabled: url !== null,
    placeholderData: keepPreviousData,
    select: opts?.select,
    ...(opts?.staleTime === undefined ? {} : { staleTime: opts.staleTime }),
    ...(opts?.gcTime === undefined ? {} : { gcTime: opts.gcTime }),
    ...(opts?.refetchOnWindowFocus === undefined ? {} : { refetchOnWindowFocus: opts.refetchOnWindowFocus }),
    ...(refetchInterval === undefined
      ? {}
      : {
          refetchInterval:
            typeof refetchInterval === 'function'
              ? (q: { state: { data: TResponse | undefined } }) => refetchInterval(q.state.data)
              : refetchInterval,
        }),
  })

  const { refetch } = query
  return {
    data: url === null ? null : (query.data ?? null),
    loading: url === null ? false : query.isLoading || query.isPlaceholderData,
    lastFetchedAt: query.dataUpdatedAt || null,
    refetch: () => {
      void refetch()
    },
    error: url === null ? null : (query.error ?? null),
  }
}
